import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import Scrollbars from "react-custom-scrollbars-2";

import {
    COLUMN_INDEX_TO_NAME,
    JournalEntryLineProxyObject,
    NUMBER_OF_CELLS_PER_LINE,
} from "../journalEntryModal/JournalEntryModal";

import {
    convertClipboardTextIntoCells,
    copyTextToClipboard,
    readTextFromClipboard,
} from "../../../lib/clipboardUtils";
import styles from "./EditableTable.module.scss";
import { EditableCellSearchOption } from "./EditableTableCellSearch";
import { EditableTableCellSelectionState } from "./EditableTableCell";
import {
    CellGrid,
    EditableCellItem,
    getCutColumns,
    getFirstCellFromSelection,
    getOnChangeCallbacks,
    getOnClickCallbacks,
    getSelectedItemsWithSelectionBorders,
    recalculateSelectableItemsCallback,
    traverseCellsToEdit,
} from "./EditableTableUtils";
import { useEditableTableContext } from "./EditableTableContext";
import { EditableTableRow } from "./EditableTableRow";
import { EditableTableBodyCore } from "./EditableTableBodyCore";

interface Props {
    tableRef: React.RefObject<HTMLDivElement>;
    scrollbarsRef: React.RefObject<Scrollbars>;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
    ) => void;
    isEditable?: boolean;
    // NOTE: for now we support only one type of search options
    // (assuming all searchable columns have the same type)
    searchOptions: EditableCellSearchOption[];
}

export type JournalEntryTableLineStateValue = string | number | null;

export const DRAGGING_THRESHOLD = 2;

export const EditableTableBody = ({
    tableRef,
    scrollbarsRef,
    setFieldValue,
    isEditable = true,
    searchOptions,
}: Props) => {
    const selectableItemsRef = useRef<CellGrid<EditableCellItem>>([]);
    const {
        selectedArea,
        setSelectedArea,
        targetLines,
        isInBuffer,
        setIsInBuffer,
        updateAndReturnNewLine,
    } = useEditableTableContext<JournalEntryLineProxyObject>();
    const linesRef = useRef(targetLines);
    const [lineToInsert, setLineToInsert] = useState<number | null>(null);

    const recalculateSelectableItems = useCallback(
        () =>
            recalculateSelectableItemsCallback(
                tableRef,
                selectableItemsRef,
                styles.editableTableCell,
            ),
        [tableRef],
    );

    useEffect(() => {
        linesRef.current = targetLines;
        recalculateSelectableItems();
    }, [targetLines, recalculateSelectableItems]);

    const previousSelectedItemsWithSelectionBorders = useRef<
        CellGrid<EditableTableCellSelectionState>
    >([]);

    const selectedItemsWithSelectionBorders: CellGrid<EditableTableCellSelectionState> =
        useMemo(
            () =>
                getSelectedItemsWithSelectionBorders({
                    selectedArea,
                    isInBuffer,
                    previousSelectedItemsWithSelectionBorders,
                    numberOfCellsPerLine: NUMBER_OF_CELLS_PER_LINE,
                    selectableItemsRef,
                }),
            [selectedArea, isInBuffer],
        );

    const editLineValue = useCallback(
        (
            lineIndex: number,
            field: keyof JournalEntryLineProxyObject,
            value: string | number | null,
        ) => {
            if (!isEditable) {
                return;
            }
            const newLine = updateAndReturnNewLine({
                line: linesRef.current[lineIndex],
                changes: [{ field, value }],
                fillEmptyFieldsAutomatically: true,
            });

            setFieldValue(`journalEntry.lines.${lineIndex}`, newLine);
        },
        [isEditable, setFieldValue, updateAndReturnNewLine],
    );

    const clearLine = useCallback(
        (lineIndex: number) => {
            if (!isEditable) {
                return;
            }
            const newLine = updateAndReturnNewLine({
                line: linesRef.current[lineIndex],
                changes: [
                    { field: "description", value: "" },
                    { field: "accountCode", value: "" },
                    { field: "debitAmount", value: null },
                    { field: "creditAmount", value: null },
                ],
                fillEmptyFieldsAutomatically: true,
            });

            setFieldValue(`journalEntry.lines.${lineIndex}`, newLine);
        },
        [isEditable, setFieldValue, updateAndReturnNewLine],
    );

    const memoizedOnChangeCallback: CellGrid<
        (value: JournalEntryTableLineStateValue) => void
    > = useMemo(
        () =>
            getOnChangeCallbacks({
                targetLines,
                editLineValue,
                columnIndexToName: COLUMN_INDEX_TO_NAME,
                numberOfCellsPerLine: NUMBER_OF_CELLS_PER_LINE,
            }),
        [editLineValue, targetLines],
    );

    const memoizedOnClickCallback = useMemo(
        () =>
            getOnClickCallbacks(
                setSelectedArea,
                NUMBER_OF_CELLS_PER_LINE,
                targetLines.length,
            ),
        [targetLines.length, setSelectedArea],
    );

    const onCut = useCallback(
        (e: any) => {
            if (!selectedArea) {
                return;
            }
            e.preventDefault();

            // NOTE: we don't want to copy or change non-editable cells
            const { effectiveToColumn, cells } = getCutColumns(
                linesRef,
                selectedArea,
                COLUMN_INDEX_TO_NAME,
            );

            copyTextToClipboard(
                cells.map((row) => row.join("	")).join("\n"),
            ).then((result) => {
                if (!result) {
                    return;
                }
                const newLines = [...linesRef.current];

                traverseCellsToEdit({
                    firstActiveCell: [
                        selectedArea.topLeftCellPosition.rowIndex,
                        selectedArea.topLeftCellPosition.columnIndex,
                    ],
                    cellsToInsert: cells,
                    lastColumnIndex: effectiveToColumn,
                    callback: (lineIndex, columnIndex) => {
                        const columnToChangeName =
                            COLUMN_INDEX_TO_NAME[columnIndex];
                        if (!columnToChangeName) {
                            return;
                        }
                        (newLines[lineIndex] as any)[columnToChangeName] = [
                            "debitAmount",
                            "creditAmount",
                        ].includes(columnToChangeName)
                            ? null
                            : "";
                    },
                });
                setFieldValue(`journalEntry.lines`, newLines);
                setIsInBuffer(false);
            });
        },
        [selectedArea, setIsInBuffer, setFieldValue, linesRef],
    );

    const findAccountByLabelOrValue = useCallback(
        (search: string | undefined) => {
            if (!search) {
                return;
            }

            const account = searchOptions.find(
                (option) =>
                    !option.isGroupLabel &&
                    (option.label.replace(`${option.value} - `, "") ===
                        search ||
                        option.value === search),
            );

            return account;
        },
        [searchOptions],
    );

    const onPaste = useCallback(
        (e: any) => {
            if (!selectedArea) {
                return;
            }
            e.preventDefault();

            readTextFromClipboard().then((text) => {
                if (text === null) {
                    return;
                }
                const cellsToInsert = convertClipboardTextIntoCells(text || "");

                const firstActiveCell = getFirstCellFromSelection(selectedArea);

                const newLines = [...linesRef.current];

                if (
                    cellsToInsert.length >
                    newLines.length - firstActiveCell[0]
                ) {
                    newLines.push(
                        ...new Array(
                            cellsToInsert.length -
                                (newLines.length - firstActiveCell[0]),
                        )
                            .fill(null)
                            .map(() => ({
                                id: Math.random().toString(),
                                accountCode: "",
                                debitAmount: null,
                                creditAmount: null,
                                description: "",
                            })),
                    );
                }

                traverseCellsToEdit({
                    firstActiveCell,
                    cellsToInsert,
                    lastColumnIndex: NUMBER_OF_CELLS_PER_LINE - 1,
                    callback: (lineIndex, columnIndex, value) => {
                        const columnToChangeName =
                            COLUMN_INDEX_TO_NAME[columnIndex];
                        if (!columnToChangeName || value === undefined) {
                            return;
                        }
                        let valueToInsert: JournalEntryTableLineStateValue =
                            value;
                        if (
                            ["debitAmount", "creditAmount"].includes(
                                columnToChangeName,
                            )
                        ) {
                            valueToInsert = value === "" ? null : Number(value);

                            if ((valueToInsert as number) === 0) {
                                return;
                            }
                        }

                        if (columnToChangeName === "accountCode") {
                            const account = findAccountByLabelOrValue(
                                valueToInsert?.toString(),
                            );
                            if (account) {
                                valueToInsert = account.value!.toString();
                            }
                        }

                        newLines[lineIndex] = updateAndReturnNewLine({
                            line: newLines[lineIndex],
                            changes: [
                                {
                                    field: columnToChangeName,
                                    value: valueToInsert,
                                },
                            ],
                        });
                    },
                });
                setFieldValue(`journalEntry.lines`, newLines);
                setIsInBuffer(false);
            });
        },
        [
            findAccountByLabelOrValue,
            selectedArea,
            setFieldValue,
            setIsInBuffer,
            linesRef,
            updateAndReturnNewLine,
        ],
    );

    const hasSelectedAllLines =
        selectedArea?.topLeftCellPosition.rowIndex === 0 &&
        selectedArea?.bottomRightCellPosition.rowIndex ===
            targetLines.length - 1;

    return (
        <EditableTableBodyCore<JournalEntryLineProxyObject>
            onCut={onCut}
            onPaste={onPaste}
            formValueName="journalEntry.lines"
            isEditable={isEditable}
            tableRef={tableRef}
            scrollbarsRef={scrollbarsRef}
            setFieldValue={setFieldValue}
            lineToInsert={lineToInsert}
            setLineToInsert={setLineToInsert}
            columnIndexToName={COLUMN_INDEX_TO_NAME}
        >
            <div ref={tableRef} className={styles.editableTableBody}>
                {targetLines.map(({ id }, lineIndex) => (
                    <EditableTableRow
                        key={id}
                        id={id}
                        lineIndex={lineIndex}
                        hasSelectedAllLines={hasSelectedAllLines}
                        onChangeCallback={memoizedOnChangeCallback[lineIndex]}
                        onClickCallback={memoizedOnClickCallback[lineIndex]}
                        searchOptions={searchOptions}
                        selectionState={
                            selectedItemsWithSelectionBorders[lineIndex]
                        }
                        insertToThisLine={lineToInsert === lineIndex}
                        isEditable={isEditable}
                        clearLine={clearLine}
                    />
                ))}
            </div>
        </EditableTableBodyCore>
    );
};
